import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertCircle } from "lucide-react";
import api, { formatGBP } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useSiteInfo } from "../context/SiteInfoContext";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const { clearCart } = useCart();
  const { info } = useSiteInfo();
  const concierge = info?.email || "hello@glitterzshc.com";
  const [state, setState] = useState({ status: "polling", payment_status: null, order: null });

  useEffect(() => {
    if (!sid) { setState({ status: "error" }); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sid}`);
        if (data.payment_status === "paid") {
          clearCart();
          setState({ status: "paid", ...data });
          return;
        }
        if (data.status === "expired") { setState({ status: "expired" }); return; }
        attempts += 1;
        // Up to 45 attempts × 2s = 90s total — gives Stripe time to settle
        if (attempts >= 45) { setState({ status: "timeout" }); return; }
        setTimeout(poll, 2000);
      } catch {
        setState({ status: "error" });
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid]);

  return (
    <main className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="checkout-success">
      {state.status === "polling" && (
        <>
          <div className="w-12 h-12 mx-auto rounded-full border-2 border-[var(--gold)] border-t-transparent animate-spin" />
          <p className="font-serif text-2xl mt-8">Confirming your order…</p>
          <p className="text-sm text-neutral-500 mt-2">Please remain on this page — this can take up to a minute.</p>
        </>
      )}
      {state.status === "paid" && (
        <>
          <CheckCircle2 className="w-14 h-14 mx-auto text-[var(--gold)]" strokeWidth={1.25} />
          <p className="eyebrow text-neutral-600 mt-6">Order confirmed</p>
          <h1 className="font-serif text-4xl md:text-5xl mt-3">Thank you.</h1>
          <p className="text-base font-light text-neutral-600 mt-4">Your order is on its way to our atelier. A confirmation email has been sent.</p>
          {state.order && (
            <div className="border border-hair text-left p-6 mt-10">
              <div className="flex justify-between mb-2 text-sm"><span className="text-neutral-500">Order</span><span data-testid="order-id">#{state.order.id?.slice(0, 8).toUpperCase()}</span></div>
              <div className="flex justify-between mb-2 text-sm"><span className="text-neutral-500">Tracking</span><span data-testid="tracking-num">{state.order.tracking_number || "Generating…"}</span></div>
              <div className="flex justify-between mb-2 text-sm"><span className="text-neutral-500">Total</span><span>{formatGBP(state.order.total)}</span></div>
            </div>
          )}
          <div className="mt-10 flex gap-4 justify-center">
            <Link to="/account" className="btn-primary" data-testid="view-orders-btn">View Orders</Link>
            <Link to="/shop" className="btn-ghost">Continue Shopping</Link>
          </div>
        </>
      )}
      {state.status === "timeout" && (
        <>
          <CheckCircle2 className="w-14 h-14 mx-auto text-[var(--gold)]" strokeWidth={1.25} />
          <h1 className="font-serif text-3xl mt-6">Payment received — confirmation pending</h1>
          <p className="text-sm text-neutral-500 mt-3 max-w-md mx-auto">
            Stripe has confirmed your payment, but we're still finalising your order. You'll receive a confirmation email at the address on your account within a few minutes. You can also view your order in your account once it lands. If you have any questions, email us at <a href={`mailto:${concierge}`} className="underline">{concierge}</a>.
          </p>
          <div className="mt-8 flex gap-4 justify-center">
            <Link to="/account" className="btn-primary">View Orders</Link>
            <Link to="/shop" className="btn-ghost">Continue Shopping</Link>
          </div>
        </>
      )}
      {(state.status === "error" || state.status === "expired") && (
        <>
          <AlertCircle className="w-14 h-14 mx-auto text-neutral-700" strokeWidth={1.25} />
          <h1 className="font-serif text-3xl mt-6">We couldn't confirm your payment</h1>
          <p className="text-sm text-neutral-500 mt-3">Please check your email for a receipt or contact us at <a href={`mailto:${concierge}`} className="underline">{concierge}</a>.</p>
          <Link to="/cart" className="btn-primary mt-8 inline-flex">Back to Cart</Link>
        </>
      )}
    </main>
  );
}
